import { MediumLargeHeadingStyled } from "src/components/custom/texts/texts.styled";
import { Button, Flex, Input, InputNumber } from "@nubeteck/components";
import { Card, Form, Table, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect } from "react";
import { useUpdateSubjectsWeightMutation } from "src/services/importsService";
import { toastErrorStyle, useSuccessSubjectsWeightUpdatedToast } from "src/constants";
import toast from "react-hot-toast";
import { getImportDataPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";
import { _translateValue } from "../translation-gestor/translateValue";

const SCREEN_NAME = "PantallaSubjectsImport";

function SubjectsImport({ isLoading, getData, data }: any) {
  const [pageNumber, setPageNumber] = React.useState(1);
  const [rowsCount, setRowsCount] = React.useState(5);
  const [subjectName, setSubjectName] = React.useState("");
  const { locale } = useLocale();
  const [importDataPage, setImportDataPage] = React.useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = React.useState<boolean>(true);
  const [hasError, setHasError] = React.useState(false);
  const [subjectsWeight, setSubjectsWeight] = React.useState<any[]>([
    { asignaturaId: data?.data?.result[0].asignaturaId, peso: data?.data?.result[0].peso },
  ]);
  const [translatedData, setTranslatedData] = React.useState<{
    asignaturas: { [key: string]: string };
    descripciones: { [key: string]: string };
  }>({
    asignaturas: {},
    descripciones: {},
  });

  const [updateSubjects, { isSccess: isSuccessSubjectsUpdated, error: errorCareersUpdated }] =
    useUpdateSubjectsWeightMutation();
  const showSuccessSubjectsWeightUpdatedToast = useSuccessSubjectsWeightUpdatedToast();

  useEffect(() => {
    if (errorCareersUpdated) {
      toast.error(JSON.parse(errorCareersUpdated?.data)?.message, toastErrorStyle);
    }
    if (isSuccessSubjectsUpdated) {
      showSuccessSubjectsWeightUpdatedToast();
    }
  }, [errorCareersUpdated, isSuccessSubjectsUpdated, showSuccessSubjectsWeightUpdatedToast]);

  useEffect(() => {
    getData({ rowsCount, page: pageNumber, asignaturaNombre: subjectName, modalidadId: null });
  }, [subjectName, rowsCount, pageNumber, getData, isSuccessSubjectsUpdated]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasData = data?.data?.result && Array.isArray(data.data.result) && data.data.result.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasData) {
        setTranslatedData({
          asignaturas: {},
          descripciones: {},
        });
        return;
      }

      const asignaturasTranslations: { [key: string]: string | null } = {};
      const descripcionesTranslations: { [key: string]: string | null } = {};

      const translationPromises: Promise<void>[] = [];

      // Recopilar todos los valores únicos que necesitan traducción
      data.data.result.forEach((record: any) => {
        // Traducir asignatura
        if (record.asignaturaNombre && !asignaturasTranslations.hasOwnProperty(record.asignaturaNombre)) {
          asignaturasTranslations[record.asignaturaNombre] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, record.asignaturaNombre, locale).then((translated) => {
              asignaturasTranslations[record.asignaturaNombre] = translated;
            })
          );
        }

        // Traducir descripción
        if (record.descripcion && !descripcionesTranslations.hasOwnProperty(record.descripcion)) {
          descripcionesTranslations[record.descripcion] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, record.descripcion, locale).then((translated) => {
              descripcionesTranslations[record.descripcion] = translated;
            })
          );
        }
      });

      await Promise.all(translationPromises);

      // Actualizar estados con las traducciones
      setTranslatedData({
        asignaturas: asignaturasTranslations as { [key: string]: string },
        descripciones: descripcionesTranslations as { [key: string]: string },
      });
    };

    translateData();
  }, [data?.data?.result, locale]);

  const changeSubjectsWeight = (peso: number, asignaturaId: number) => {
    const arr = subjectsWeight;
    const indexFound = subjectsWeight.indexOf(
      subjectsWeight.find((sub) => sub.asignaturaId === asignaturaId),
    );
    if (indexFound === -1) {
      arr.push({ asignaturaId, peso });
    } else {
      arr[indexFound] = { asignaturaId, peso };
    }
    setSubjectsWeight(arr);
  };

  const actualizarMaterias = () => {
    const arr = subjectsWeight;
    arr.shift();
    updateSubjects(arr);
  };

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setImportDataPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getImportDataPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setImportDataPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setImportDataPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const columns = React.useMemo<ColumnsType<any>>(
    () => [
      {
        title: importDataPage?.subject_Import_table ?.[0]?.label || "Código",
        dataIndex: "asignaturaCodigo",
      },
      {
        title: importDataPage?.subject_Import_table ?.[1]?.label || "Asignatura",
        dataIndex: "asignaturaNombre",
        render: (text: string) => translatedData.asignaturas[text] || text,
      },
      {
        title: importDataPage?.subject_Import_table ?.[2]?.label || "Costo",
        dataIndex: "costo",
      },
      {
        title: importDataPage?.subject_Import_table ?.[3]?.label || "Descripción",
        dataIndex: "descripcion",
        render: (text: string) => translatedData.descripciones[text] || text,
      },
      {
        title: importDataPage?.subject_Import_table ?.[4]?.label || "Créditos",
        dataIndex: "creditos",
      },
      {
        title: importDataPage?.subject_Import_table ?.[5]?.label || "Peso",
        dataIndex: "asignaturaId",
        render: (value) => (
          <InputNumber
            size="small"
            placeholder={importDataPage?.weight_placeholder || "Peso"}
            type="number"
            min={0}
            max={100}
            controls={false}
            defaultValue={data?.data?.result?.find((c: any) => c.asignaturaId === value)?.peso}
            onChange={(e) => changeSubjectsWeight(e ? +e : 0, value)}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, importDataPage?.subject_Import_table, translatedData],
  );

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setImportDataPage(null);
      }}
    />;
  }

  return (
    <>
      {isLoadingContent ? (
        <>
          <Skeleton.Input active size="large" style={{ width: 250, marginBottom: 16 }} />
          <Flex style={{ gap: "15px", marginBottom: 16 }}>
            <Skeleton.Input active size="default" style={{ width: 300 }} />
            <Skeleton.Input active size="default" style={{ width: 150 }} />
          </Flex>
          <Card bodyStyle={{ padding: 0, width: "100%" }}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </Card>
        </>
      ) : (
        <>
          <MediumLargeHeadingStyled>{importDataPage?.subject_Import.title || "Asignaturas importadas"}</MediumLargeHeadingStyled>
          <Flex style={{ gap: "15px" }}>
            <Form.Item label={importDataPage?.subject_Import.search_input || "Búsqueda por nombre de asignatura"}>
              <Input
                type="text"
                onChange={(e) => setSubjectName(e.target.value)}
                placeholder={importDataPage?.subject_Import.search_input || "Búsqueda por nombre de asignatura"}
              />
            </Form.Item>
            {subjectsWeight.length > 0 && !isLoading ? (
              <Button
                type="primary"
                onClick={() => {
                  actualizarMaterias();
                }}
              >
                {importDataPage?.subject_Import.save_button || "Guardar pesos"}
              </Button>
            ) : null}
          </Flex>

          <Card bodyStyle={{ padding: 0, width: "100%" }}>
            <Table<any>
              loading={isLoading}
              columns={columns}
              dataSource={data?.data?.result}
              rowKey={(v) => v.asignaturaId}
              pagination={{
                onChange(page, pageSize) {
                  setPageNumber(page);
                  setRowsCount(pageSize);
                },
                pageSize: rowsCount,
                total: data?.data?.pageCount * rowsCount,
                current: pageNumber,
                showSizeChanger: true,
                style: { marginRight: "30px" },
              }}
              scroll={{ x: 800 }}
            />
          </Card>
        </>
      )}
    </>
  );
}

export default SubjectsImport;
